import React from 'react';
import { addDays, setHours, setMinutes } from 'date-fns';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';

import { AuthContext } from '../../context/AuthContext';
import { EVENT_NAMES } from '../../utils/eventLogging/eventNames';
import { firestore } from '../../context/FirebaseConfig';
import { useTranslation } from '../../i18n/i18n';
import Select from '../ui/Select';
import UserModalShell from './UserModalShell';
import { FEED_SHIFTS } from './constants';

function buildUserOptions(users) {
  if (!users || Object.keys(users).length === 0) return [];

  return Object.entries(users)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([key, value]) => ({
      value: key,
      label: `${value?.firstname ?? ''} ${value?.lastname ?? ''}`.trim(),
    }));
}

export default function SwitchUserModal({ settings, onClose }) {
  const { currentUser, logEventCustom } = React.useContext(AuthContext);
  const { t } = useTranslation(currentUser?.info?.languageCode);
  const [shiftInfo, setShiftInfo] = React.useState({
    user: '',
    feed_shift: 'A',
    work_shift: 0,
  });

  React.useEffect(() => {
    const deviceDoc = doc(firestore, 'clients', currentUser.company.company, 'devices', currentUser.device_id);
    return onSnapshot(deviceDoc, (snapshot) => {
      if (!snapshot.exists || !snapshot.data() || !('lastPlantFeedShiftInfo' in snapshot.data())) {
        return;
      }

      const snapShiftInfo = snapshot.data().lastPlantFeedShiftInfo;
      const shifts = currentUser.company.shifts || [];
      const workShiftName =
        typeof snapShiftInfo.work_shift === 'object' ? snapShiftInfo.work_shift?.name : snapShiftInfo.work_shift;
      const shiftIndex = shifts.findIndex((shift) => shift.name === workShiftName);
      setShiftInfo({ ...snapShiftInfo, work_shift: shiftIndex === -1 ? 0 : shiftIndex });
    });
  }, [currentUser.company.company, currentUser.company.shifts, currentUser.device_id]);

  function handleChange(key, value) {
    setShiftInfo((prev) => ({ ...prev, [key]: value }));
  }

  function saveAndClose() {
    const shifts = currentUser.company.shifts || [];
    const activeShift = shifts[shiftInfo.work_shift];
    if (!activeShift) return;
    const [hours, minutes] = activeShift.end_time.split(':').map(Number);
    let shiftExpiration = new Date();
    shiftExpiration = setHours(shiftExpiration, hours);
    shiftExpiration = setMinutes(shiftExpiration, minutes);

    if (shiftExpiration < new Date()) {
      shiftExpiration = addDays(shiftExpiration, 1);
    }

    const deviceDoc = doc(firestore, 'clients', currentUser.company.company, 'devices', currentUser.device_id);
    updateDoc(deviceDoc, {
      lastPlantFeedShiftInfo: {
        ...shiftInfo,
        work_shift: activeShift,
        shift_expiration_timestamp: shiftExpiration,
      },
    });

    logEventCustom(EVENT_NAMES.OPERATOR_SWITCH, {
      operator_id: shiftInfo.user || null,
      work_shift: activeShift.name,
      feed_shift: shiftInfo.feed_shift || null,
    });

    onClose?.();
  }

  const userOptions = buildUserOptions(settings?.users);
  const workShiftOptions = (currentUser.company.shifts || []).map((shift, index) => ({
    value: String(index),
    label: shift.name,
  }));
  const feedShiftOptions = FEED_SHIFTS.map((shift) => ({ value: shift, label: shift }));

  return (
    <UserModalShell
      title={t('switch_user_modal_title')}
      onClose={onClose}
      footer={
        <button
          type='button'
          className='flex items-center justify-center text-lg leading-[120%] font-medium text-[#FFFFFF] dark:text-[#FAFAFA] rounded-lg bg-[#2563EB] hover:bg-[#1E4FBB] active:bg-[#113C91] dark:bg-[#2773B7] dark:hover:bg-[#1D649C] dark:active:bg-[#2773B7] py-2 px-4 min-h-12 box-border transition-all duration-300'
          onClick={saveAndClose}
        >
          {t('switch_user_modal_action')}
        </button>
      }
    >
      <div className='flex flex-col gap-1.5'>
        <label className='text-base leading-[120%] text-[#464255] dark:text-[#FAFAFA]'>
          {t('switch_user_modal_user')}
        </label>
        <Select
          className='w-full'
          placeholder={t('switch_user_modal_user')}
          selectedValue={shiftInfo.user ? undefined : t('switch_user_modal_user')}
          value={shiftInfo.user}
          options={userOptions}
          onChange={(value) => handleChange('user', value)}
        />
      </div>

      <div className='flex flex-col gap-1.5'>
        <label className='text-base leading-[120%] text-[#464255] dark:text-[#FAFAFA]'>
          {t('switch_user_modal_work_shift')}
        </label>
        <Select
          className='w-full'
          placeholder={t('switch_user_modal_work_shift')}
          value={String(shiftInfo.work_shift)}
          options={workShiftOptions}
          onChange={(value) => handleChange('work_shift', Number(value))}
        />
      </div>

      <div className='flex flex-col gap-1.5'>
        <label className='text-base leading-[120%] text-[#464255] dark:text-[#FAFAFA]'>
          {t('switch_user_modal_production_shift')}
        </label>
        <Select
          className='w-full'
          placeholder={t('switch_user_modal_production_shift')}
          value={shiftInfo.feed_shift}
          options={feedShiftOptions}
          onChange={(value) => handleChange('feed_shift', value)}
        />
      </div>
    </UserModalShell>
  );
}
